import { boardSocketUrl } from '../lib/api';
import type { ClientMsg, Op, ServerMsg } from '@scribbleplot/shared';

/**
 * Realtime channel for one board.
 *
 * Owns reconnection so the store never has to think about socket lifecycle.
 * On every (re)connect the server replies with `hello`, carrying the
 * authoritative document — which doubles as the recovery path after a drop.
 */

export type SocketStatus = 'connecting' | 'live' | 'offline';

export type SocketHandlers = {
  onMessage: (msg: ServerMsg) => void;
  onStatus: (status: SocketStatus) => void;
  onPending?: (count: number) => void;
};

const BASE_RETRY_MS = 600;
const MAX_RETRY_MS = 15_000;
/** Keeps intermediaries from idling the connection out. */
const PING_MS = 25_000;

export class BoardSocket {
  private ws: WebSocket | null = null;
  private retries = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private closed = false;
  private selfId: string | null = null;
  private seq = 0;
  /** Ops sent but not yet acknowledged, so a drop mid-flight can be resent. */
  private pending = new Map<number, Op[]>();

  constructor(
    private readonly boardId: string,
    private readonly handlers: SocketHandlers,
  ) {}

  connect(): void {
    if (this.closed) return;
    this.clearTimers();
    this.handlers.onStatus(this.retries === 0 ? 'connecting' : 'offline');

    let ws: WebSocket;
    try {
      ws = new WebSocket(boardSocketUrl(this.boardId));
    } catch {
      this.scheduleRetry();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      this.retries = 0;
      this.pingTimer = setInterval(() => this.send({ t: 'ping' }), PING_MS);
      // Replay anything the previous connection never got to acknowledge.
      for (const [seq, ops] of this.pending) this.raw({ t: 'ops', seq, ops });
    };

    ws.onmessage = (event) => {
      let msg: ServerMsg;
      try {
        msg = JSON.parse(String(event.data)) as ServerMsg;
      } catch {
        return;
      }
      if (msg.t === 'hello') {
        this.selfId = msg.self.id;
        this.handlers.onStatus('live');
      }
      if (msg.t === 'ops' && msg.seq !== null && msg.origin === this.selfId) this.pending.delete(msg.seq);
      if (msg.t === 'reject') this.pending.delete(msg.seq);
      this.handlers.onPending?.(this.pending.size);
      this.handlers.onMessage(msg);
    };

    ws.onerror = () => {
      // `onclose` always follows; retry logic lives there.
    };

    ws.onclose = (event) => {
      this.clearTimers();
      this.ws = null;
      if (this.closed) return;
      // 4003/4004 mean the board is gone or forbidden — retrying cannot help.
      if (event.code === 4003 || event.code === 4004) {
        this.handlers.onStatus('offline');
        return;
      }
      this.handlers.onStatus('offline');
      this.scheduleRetry();
    };
  }

  private scheduleRetry(): void {
    if (this.closed) return;
    const backoff = Math.min(MAX_RETRY_MS, BASE_RETRY_MS * 2 ** this.retries);
    // Jitter stops every client from reconnecting on the same tick.
    const delay = backoff * (0.7 + Math.random() * 0.6);
    this.retries++;
    this.retryTimer = setTimeout(() => this.connect(), delay);
  }

  private clearTimers(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.pingTimer = null;
    this.retryTimer = null;
  }

  private raw(msg: ClientMsg): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN) return false;
    this.ws.send(JSON.stringify(msg));
    return true;
  }

  private send(msg: ClientMsg): boolean {
    return this.raw(msg);
  }

  /** Queue ops for the server. Returns the sequence number assigned. */
  sendOps(ops: Op[]): number {
    const seq = ++this.seq;
    this.pending.set(seq, ops);
    this.handlers.onPending?.(this.pending.size);
    this.raw({ t: 'ops', seq, ops });
    return seq;
  }

  sendCursor(pos: { x: number; y: number } | null): void {
    this.send({ t: 'cursor', pos });
  }

  requestSync(): void {
    this.pending.clear();
    this.handlers.onPending?.(0);
    this.send({ t: 'resync' });
  }

  close(): void {
    this.closed = true;
    this.clearTimers();
    this.pending.clear();
    this.handlers.onPending?.(0);
    this.ws?.close(1000, 'leaving board');
    this.ws = null;
  }
}
